import { Button } from '@jouzen/ecom-components/v3/Button';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { ListItem } from '@jouzen/ecom-components/v3/ListItem';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useLocale, useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import FooterCta from '@/app/components/FooterCta';
import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import SectionHeading from '@/app/components/SectionHeading';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

import { scienceAndResearchCalloutData } from '../_data/scienceAndResearchCalloutData';

const ScienceAndResearchCallout = () => {
  const t = useTranslations();
  const locale = useLocale();
  const localePartialUrl = getLocalePartialUrl(locale);

  const handleLinkClick = () => {
    const pathname = `https://ouraring.com/blog${localePartialUrl}how-accurate-is-oura`;

    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'read_more',
      location: 'science and research',
      action: 'goes to blog',
      path: pathname,
    });

    window.open(pathname, '_blank');
  };

  return (
    <section className="pt-20" data-cy="science-and-research-callout">
      <div className="hidden md:block">
        <FooterCta
          bulletPoints={scienceAndResearchCalloutData.bulletPoints}
          buttons={[
            {
              label: 'learn_more',
              variant: 'secondary-light',
              onClick: handleLinkClick,
            },
          ]}
          description={scienceAndResearchCalloutData.description}
          eyebrow={scienceAndResearchCalloutData.eyebrow}
          indentation="pl-32 md:pl-48 lg:pl-72"
          image={{
            src: 'blue-sky/science-and-research/science-callout-or4-desktop',
          }}
          imageClassName="md:h-[600px]"
          title={scienceAndResearchCalloutData.title}
          variant={{ sm: 'background', lg: 'background' }}
        />
      </div>
      <LayoutGrid className="md:hidden">
        <GridItem
          colStart={{
            sm: 'main',
            md: 'main',
          }}
          colEnd={{
            sm: 'main',
            md: 'main',
          }}
        >
          <div className="relative mb-2 aspect-square overflow-hidden rounded-lg">
            <ArtDirectionImage
              className="object-cover"
              fill
              srcSet={[
                {
                  src: 'blue-sky/science-and-research/science-callout-or4-mobile',
                  media: `(max-width: ${BREAKPOINTS.large - 1}px)`,
                },
              ]}
            />
          </div>
        </GridItem>
        <GridItem
          colStart={{
            sm: 'main',
            md: 'main',
          }}
          colEnd={{
            sm: 'main',
            md: 'main',
          }}
        >
          <SectionHeading
            animated
            content={scienceAndResearchCalloutData.title}
            emAnimationProps={{
              transition: { delay: 0.5, duration: 1, type: 'tween' },
              viewport: { once: true },
            }}
            indentation={'pl-48'}
            multiline
            typographyProps={{
              className:
                'mb-6 mt-2 leading-tight tracking-tighter md:justify-start',
              color: 'inherit',
              Element: 'h3',
              variant: 'h2',
            }}
            wrapperAnimationProps={{
              initial: { filter: 'blur(5px)', opacity: 0 },
              transition: { delay: 0.2, duration: 0.75, type: 'tween' },
            }}
          />
          <Motion
            initial={{ opacity: 0 }}
            transition={{ delay: 0.35, duration: 0.5, type: 'tween' }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            <Typography
              className="my-2 font-bold uppercase"
              color="inherit"
              variant={'eyebrow-large'}
            >
              {t(scienceAndResearchCalloutData.eyebrow)}
            </Typography>
            <Typography color="inherit">
              <RichText
                messageKey={scienceAndResearchCalloutData.description}
              />
            </Typography>
            <div className="mb-4">
              {scienceAndResearchCalloutData.bulletPoints.map((key, index) => (
                <ListItem
                  className={cx('my-2 ml-2', {
                    'mt-4': index === 0,
                  })}
                  key={key}
                >
                  <Typography
                    color="inherit"
                    variant="body-small"
                    className="inline"
                  >
                    {t(key)}
                  </Typography>
                </ListItem>
              ))}
            </div>
            <Button
              data-cy="mobile-action-button"
              onClick={handleLinkClick}
              size="small"
              variant="secondary-dark"
            >
              {t('learn_more')}
            </Button>
          </Motion>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default ScienceAndResearchCallout;
